import { useCallback, useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { fetchActiveSalespersonCodes } from '../../lib/applicationFormSettings';
import { publicAccountCreationUrl } from '../../types/applicationForm';

type Props = {
  open: boolean;
  onClose: () => void;
};

export function CopySalespersonApplyLinksModal({ open, onClose }: Props) {
  const [codes, setCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCodes(await fetchActiveSalespersonCodes());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load salesperson codes.');
      setCodes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setCopiedCode(null);
    void load();
  }, [open, load]);

  const copyLink = async (code: string) => {
    const url = publicAccountCreationUrl(code);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCode(code);
      window.setTimeout(() => setCopiedCode((prev) => (prev === code ? null : prev)), 2000);
    } catch {
      window.prompt('Copy apply link:', url);
    }
  };

  return (
    <Modal title="Copy salesperson apply links" open={open} onClose={onClose} wide>
      <p className="page-lead" style={{ marginBottom: 12 }}>
        Each salesperson has a dedicated customer link. Submissions via that link auto-fill their
        salesperson code — AR can still change it when reviewing.
      </p>
      {error ? <p className="alert alert-error">{error}</p> : null}
      {loading ? (
        <p className="page-lead">Loading codes…</p>
      ) : codes.length === 0 ? (
        <p className="page-lead">No active salesperson codes yet. Add them under User management.</p>
      ) : (
        <div className="settings-table-wrap">
          <table className="settings-table">
            <thead>
              <tr>
                <th>Salesperson</th>
                <th aria-label="Copy" />
              </tr>
            </thead>
            <tbody>
              {codes.map((code) => (
                <tr key={code}>
                  <td>
                    <code>{code}</code>
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => void copyLink(code)}
                    >
                      {copiedCode === code ? 'Copied' : 'Copy link'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="unified-users-create__footer" style={{ marginTop: 16 }}>
        <button type="button" className="btn-secondary btn-sm" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}
